import { Component, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule, ActivatedRoute } from '@angular/router';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Auth } from '@angular/fire/auth';
import { AuthService } from '../../core/services/auth.service';
import { InvitationService } from '../../core/data-services/invitation.service';
import { FormInputComponent } from '../../shared/components/form-input/form-input.component';
import { FormPasswordComponent } from '../../shared/components/form-password/form-password.component';
import { FormButtonComponent } from '../../shared/components/form-button/form-button.component';
import { LEGAL_URLS } from '../../shared/constants/legal-urls';
import { AuthThemeDirective } from 'src/app/core/directives/auth-theme-directive';
import { BRAND_ASSETS } from 'src/app/core/constants/brand-assets';

/**
 * Login Page Component
 * Email/password authentication with SSO options
 * Based on: agent-os/design/mockups/onboarding/new-user/steps-1-3-login-signup-email-sent.png
 */
@Component({
  selector: 'app-login',
  standalone: true,
  imports: [CommonModule, RouterModule, FormsModule, ReactiveFormsModule, FormInputComponent, FormPasswordComponent, FormButtonComponent, AuthThemeDirective],
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss']
})
export class LoginComponent implements OnInit {
  readonly logo = BRAND_ASSETS.logoShieldOnDark;

  readonly legalUrls = LEGAL_URLS;
  loginForm: FormGroup;
  loading: boolean = false;
  errorMessage: string = '';
  pendingInvitationMessage: string = '';
  invitationId: string | null = null;
  showResendVerification: boolean = false;
  verificationSent: boolean = false;
  sendingVerification: boolean = false;
  private auth = inject(Auth);

  constructor(
    private fb: FormBuilder,
    private authService: AuthService,
    private invitationService: InvitationService,
    private router: Router,
    private route: ActivatedRoute
  ) {
    this.loginForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
      password: ['', [Validators.required, Validators.minLength(6)]]
    });
  }

  ngOnInit(): void {
    // Check for invitation ID in query params
    this.route.queryParams.subscribe(params => {
      if (params['invitationId']) {
        this.invitationId = params['invitationId'];
        this.pendingInvitationMessage = 'Please login to accept your organization invitation.';
      }

      // Set by the auth interceptor after the Hub rejects a flagged account.
      if (params['reason'] === 'password-reset-required') {
        this.errorMessage = 'We found your account, but you need to set a new password before signing in. Use "Forgot password?" below to get a reset link.';
      }
    });
  }

  /**
   * Handle form submission
   */
  async onSubmit(): Promise<void> {
    if (this.loginForm.invalid) {
      return;
    }

    this.loading = true;
    this.errorMessage = '';
    this.showResendVerification = false;
    this.verificationSent = false;

    try {
      const { email, password } = this.loginForm.value;
      await this.authService.signIn(email, password);

      // DEBUG: Log user object and token claims
      const currentUser = this.authService.getCurrentUser();
      console.log('=== LOGIN DEBUG ===');
      console.log('Current User Object:', currentUser);
      console.log('User UID:', currentUser?.uid);
      console.log('User Email:', currentUser?.email);
      console.log('Custom Claims from user object:', currentUser?.customClaims);

      // Get and decode the Firebase ID token directly from Firebase Auth
      const firebaseUser = this.auth.currentUser;
      if (firebaseUser) {
        const idTokenResult = await firebaseUser.getIdTokenResult();
        console.log('=== FIREBASE TOKEN ===');
        console.log('Full Token Result:', idTokenResult);
        console.log('All Token Claims:', idTokenResult.claims);
        console.log('Custom Claims (if present):', idTokenResult.claims['c'], idTokenResult.claims['m']);
        console.log('Token Issued At:', new Date(idTokenResult.issuedAtTime));
        console.log('Token Expiration:', new Date(idTokenResult.expirationTime));

        // Force token refresh to get latest claims
        console.log('=== REFRESHING TOKEN ===');
        const refreshedToken = await firebaseUser.getIdTokenResult(true);
        console.log('Refreshed Token Claims:', refreshedToken.claims);
        console.log('Refreshed Custom Claims:', refreshedToken.claims['c'], refreshedToken.claims['m']);
      }
      console.log('===================');

      // Unverified accounts can't reach the app. Handled here as well as in
      // AuthGuard so the user lands on /verify/email directly instead of
      // flashing a dashboard route on the way there.
      //
      // A pending invitation is still accepted before diverting — it's a Hub
      // call that needs no UI, and skipping it would silently cost them the
      // org membership they followed the link for.
      const signedInUser = this.authService.getCurrentUser();
      if (signedInUser && !signedInUser.emailVerified) {
        if (this.invitationId) {
          try {
            await this.invitationService.acceptInvitation(this.invitationId);
          } catch (invitationError: any) {
            console.error('Failed to accept invitation:', invitationError);
          }
        }
        this.router.navigate(['/verify/email'], { state: { email: signedInUser.email, next: this.invitationId ? '/team' : '' } });
        return;
      }

      // If there's a pending invitation, accept it before navigating
      if (this.invitationId) {
        try {
          console.log('Accepting invitation:', this.invitationId);
          await this.invitationService.acceptInvitation(this.invitationId);
          console.log('Invitation accepted successfully');
          // Navigate to team page to show the new organization
          this.router.navigate(['/team']);
        } catch (invitationError: any) {
          console.error('Failed to accept invitation:', invitationError);
          this.errorMessage = `Login successful, but failed to accept invitation: ${invitationError.message || 'Unknown error'}`;
          // Still navigate to dashboard even if invitation acceptance fails
          setTimeout(() => {
            this.router.navigate(['/dashboard']);
          }, 3000);
        }
      } else {
        this.router.navigate(['/dashboard']);
      }
    } catch (error: any) {
      this.errorMessage = error.message;
      // Check if error is due to email not being verified
      if (error.message?.toLowerCase().includes('verify your email')) {
        this.showResendVerification = true;
      }
    } finally {
      this.loading = false;
    }
  }

  /**
   * Resend verification email to the user
   */
  async resendVerificationEmail(): Promise<void> {
    this.sendingVerification = true;
    this.verificationSent = false;

    try {
      await this.authService.resendEmailVerification();
      this.verificationSent = true;
      this.errorMessage = '';
    } catch (error: any) {
      this.errorMessage = error.message || 'Failed to send verification email. Please try again.';
    } finally {
      this.sendingVerification = false;
    }
  }

  /**
   * Get form control error message
   */
  getErrorMessage(controlName: string): string {
    const control = this.loginForm.get(controlName);

    if (control?.hasError('required')) {
      return `${controlName.charAt(0).toUpperCase() + controlName.slice(1)} is required`;
    }

    if (control?.hasError('email')) {
      return 'Invalid email address';
    }

    if (control?.hasError('minlength')) {
      return 'Password must be at least 6 characters';
    }

    return '';
  }

  /**
   * Check if form control has error
   */
  hasError(controlName: string): boolean {
    const control = this.loginForm.get(controlName);
    return !!(control && control.invalid && control.touched);
  }

  signInWithPhone() {
    this.router.navigate(['/login/sms'])
  }

  signInWithEmail() {
    this.router.navigate(['/login/email'])
  }
}
